/**
 * Shared data + mutation hooks for CR2 documents. Centralises the 3-step upload
 * (initiate → PUT-to-R2 → confirm), list/gcu fetching, delete, and analyze so
 * IntakeUpload (cr2a) and Documents (cr2b) stay consistent.
 *
 * The R2 PUT is cross-origin and goes DIRECT to the presigned URL — it does NOT
 * pass through our `api` client (no cookies, no Content-Type header, raw bytes).
 */
import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type {
  DocumentSummary,
  GcuProcessState,
  InitiateUploadResult,
  DocType,
} from '@klinik/shared';
import { api } from './api';
import { contentTypeFor } from './docs';

/** Outcome of a single-file upload attempt. */
export type UploadOutcome =
  | { ok: true; document: DocumentSummary }
  | { ok: false; message: string };

/**
 * Run the full upload pipeline for one file. Throws nothing — always resolves
 * to an UploadOutcome so callers can render per-tile error state.
 */
export async function uploadDocument(
  companyId: string,
  docType: DocType,
  file: File,
): Promise<UploadOutcome> {
  // 1. initiate — reserve a row + presigned PUT URL.
  const init = await api.post<InitiateUploadResult>(`/companies/${companyId}/documents`, {
    docType,
    filename: file.name,
    contentType: contentTypeFor(file),
    sizeBytes: file.size,
  });
  if (!init.ok) return { ok: false, message: init.error.message };

  // 2. PUT raw bytes straight to R2 (cross-origin, no api client, no signed
  //    Content-Type header — the presigned URL already authorises the write).
  //    On failure, best-effort delete the reserved row so it doesn't linger as
  //    an orphaned `pending_upload` (reviewer #1).
  try {
    const put = await fetch(init.data.uploadUrl, { method: 'PUT', body: file });
    if (!put.ok) {
      void deleteDocument(companyId, init.data.documentId);
      return { ok: false, message: `Gagal mengunggah ke penyimpanan (HTTP ${put.status}).` };
    }
  } catch {
    void deleteDocument(companyId, init.data.documentId);
    return { ok: false, message: 'Gagal terhubung ke penyimpanan. Coba lagi.' };
  }

  // 3. confirm — server HEAD-verifies the object then flips to "uploaded".
  const confirm = await api.post<{ document: DocumentSummary }>(
    `/companies/${companyId}/documents/${init.data.documentId}/confirm`,
  );
  if (!confirm.ok) return { ok: false, message: confirm.error.message };
  return { ok: true, document: confirm.data.document };
}

interface DocumentsState {
  documents: DocumentSummary[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  setDocuments: Dispatch<SetStateAction<DocumentSummary[]>>;
}

/** Fetch the company's document list (optionally filtered by docType/status). */
export function useDocuments(
  companyId: string | null,
  filter?: { docType?: DocType; status?: string },
): DocumentsState {
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const docType = filter?.docType;
  const status = filter?.status;

  const reload = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setError(null);
    const qs = new URLSearchParams();
    if (docType) qs.set('docType', docType);
    if (status) qs.set('status', status);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await api.get<{ documents: DocumentSummary[] }>(
      `/companies/${companyId}/documents${suffix}`,
    );
    if (res.ok) setDocuments(res.data.documents);
    else setError(res.error.message);
    setLoading(false);
  }, [companyId, docType, status]);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload]);

  return { documents, loading, error, reload, setDocuments };
}

interface GcuState {
  gcu: GcuProcessState | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/** Fetch the company's GCU process state (drives the stepper + intake summary). */
export function useGcu(companyId: string | null): GcuState {
  const [gcu, setGcu] = useState<GcuProcessState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setError(null);
    const res = await api.get<GcuProcessState>(`/companies/${companyId}/gcu`);
    if (res.ok) setGcu(res.data);
    else setError(res.error.message);
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    setLoading(true);
    void reload();
  }, [reload]);

  return { gcu, loading, error, reload };
}

/** Open a document via a short-lived presigned GET URL (new tab). */
export async function openDocument(
  companyId: string,
  docId: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await api.get<{ url: string }>(`/companies/${companyId}/documents/${docId}/url`);
  if (!res.ok) return { ok: false, message: res.error.message };
  window.open(res.data.url, '_blank', 'noopener,noreferrer');
  return { ok: true };
}

/** Soft-delete a document. */
export async function deleteDocument(
  companyId: string,
  docId: string,
): Promise<{ ok: true } | { ok: false; message: string }> {
  const res = await api.del<{ deleted: true }>(`/companies/${companyId}/documents/${docId}`);
  return res.ok ? { ok: true } : { ok: false, message: res.error.message };
}

/** Enqueue all uploaded docs for AI analysis. */
export async function analyzeDocuments(
  companyId: string,
): Promise<{ ok: true; enqueued: number; skipped: number } | { ok: false; message: string }> {
  const res = await api.post<{ enqueued: number; skipped: number }>(
    `/companies/${companyId}/documents/analyze`,
  );
  return res.ok ? { ok: true, ...res.data } : { ok: false, message: res.error.message };
}
